import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiBook } from 'react-icons/fi';

const ProgramForm = ({ open, loading, editingProgram, programTypes, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    programTypeId: '',
    name: '',
    code: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingProgram) {
      setForm({
        programTypeId: editingProgram.programTypeId?._id || editingProgram.programTypeId || '',
        name: editingProgram.name || '',
        code: editingProgram.code || ''
      });
    } else {
      setForm({
        programTypeId: '',
        name: '',
        code: ''
      });
    }
    setErrors({});
  }, [editingProgram, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.programTypeId) {
      newErrors.programTypeId = 'Program type is required';
    }

    if (!form.name.trim()) {
      newErrors.name = 'Program name is required';
    } else if (form.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!form.code.trim()) {
      newErrors.code = 'Program code is required';
    } else if (!/^[A-Za-z0-9-_ ]+$/.test(form.code.trim())) {
      newErrors.code = 'Code can only contain letters, numbers, hyphens, underscores, and spaces';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...form,
        code: form.code.trim().toUpperCase()
      });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FiBook className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {editingProgram ? 'Edit Program' : 'Create Program'}
              </h2>
              <p className="text-sm text-gray-600">
                {editingProgram ? 'Update the program details' : 'Define a new program under a program type'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-4">
            {/* Program Type */}
            <div>
              <label htmlFor="programTypeId" className="block text-sm font-medium text-gray-700 mb-1">
                Program Type *
              </label>
              <select
                id="programTypeId"
                name="programTypeId"
                value={form.programTypeId}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                  errors.programTypeId ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              >
                <option value="">Select Program Type</option>
                {programTypes
                  .filter(type => type.status === 'active')
                  .map(type => (
                    <option key={type._id} value={type._id}>
                      [{type.code}] {type.name}
                    </option>
                  ))
                }
              </select>
              {errors.programTypeId && (
                <p className="mt-1 text-sm text-red-600">{errors.programTypeId}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Program Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g., Computer Science, Mechanical Engineering"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Code */}
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                Program Code *
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="e.g., CSE, MECH, BBA"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                  errors.code ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
                maxLength={12}
                autoCapitalize="characters"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <FiSave className="w-4 h-4" />
              {loading ? 'Saving...' : editingProgram ? 'Update Program' : 'Create Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgramForm;